import { motion, useMotionValue, useTransform } from 'framer-motion'
import { ArrowRight, LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface BentoGridProps {
  className?: string
  children: React.ReactNode
}

export function BentoGrid({ className, children }: BentoGridProps) {
  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-3 gap-4 max-w-7xl mx-auto', className)}>
      {children}
    </div>
  )
}

interface TiltCardProps {
  children: React.ReactNode
  className?: string
}

export function TiltCard({ children, className }: TiltCardProps) {
  const x = useMotionValue(0)
  const y = useMotionValue(0)

  const mouseX = useTransform(x, [-0.5, 0.5], ['15deg', '-15deg'])
  const mouseY = useTransform(y, [-0.5, 0.5], ['-15deg', '15deg'])

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const width = rect.width
    const height = rect.height
    const mouseXPos = e.clientX - rect.left
    const mouseYPos = e.clientY - rect.top
    const xPct = mouseXPos / width - 0.5
    const yPct = mouseYPos / height - 0.5
    x.set(xPct)
    y.set(yPct)
  }

  const handleMouseLeave = () => {
    x.set(0)
    y.set(0)
  }

  return (
    <motion.div
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        rotateY: mouseX,
        rotateX: mouseY,
        transformStyle: 'preserve-3d',
      }}
      className={cn('relative h-full transition-all duration-200 ease-out', className)}
    >
      <div style={{ transform: 'translateZ(50px)' }} className="h-full">
        {children}
      </div>
    </motion.div>
  )
}

interface BentoCardProps {
  className?: string
  title: string
  description: string
  icon: LucideIcon
  bgImage?: string
  index?: number
  href?: string
}

export function BentoCard({
  className,
  title,
  description,
  icon: Icon,
  bgImage,
  index = 0,
  href,
}: BentoCardProps) {
  const CardWrapper = href ? 'a' : 'div'

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className={cn('group perspective-1000', className)}
    >
      <TiltCard className="h-full rounded-3xl overflow-hidden bg-slate-900/40 border border-white/5 hover:border-white/10 shadow-xl">
        <CardWrapper
          {...(href ? { href } : {})}
          className="block h-full relative"
        >
          {/* Background Gradient/Image */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-950/60 to-slate-950 z-10" />
          {bgImage && (
            <div
              className="absolute inset-0 opacity-30 group-hover:opacity-50 transition-all duration-700 bg-cover bg-center"
              style={{ backgroundImage: `url(${bgImage})` }}
            />
          )}
          {!bgImage && (
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          )}

          {/* Content */}
          <div className="relative z-20 p-6 md:p-8 h-full flex flex-col">
            <div className="mb-auto">
              <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-white mb-6 group-hover:bg-primary/20 group-hover:text-primary-300 group-hover:border-primary/30 transition-all duration-300 backdrop-blur-md shadow-lg">
                <Icon size={24} />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-3 leading-tight drop-shadow-md">
                {title}
              </h3>
              <p className="text-slate-400 text-sm md:text-base leading-relaxed max-w-[90%] drop-shadow-sm">
                {description}
              </p>
            </div>

            {/* Hover Action */}
            <div className="mt-8 flex items-center gap-2 text-sm font-bold text-secondary opacity-0 transform translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
              <span>Learn More</span>
              <ArrowRight size={16} />
            </div>
          </div>
        </CardWrapper>
      </TiltCard>
    </motion.div>
  )
}

export default BentoGrid
